import React from 'react';
import { fabric } from 'fabric';
import { useTranslation } from 'react-i18next';
import {
    AlignCenter,
    AlignHorizontalJustifyCenter,
    AlignLeft,
    AlignRight,
    AlignVerticalJustifyCenter,
    Bold,
    CaseSensitive,
    Copy,
    Crop,
    FlipHorizontal2,
    Group,
    Italic,
    Lock,
    LockOpen,
    Trash2,
    Ungroup,
    Wallpaper,
} from 'lucide-react';

import { isImageObject, isTextObject, primaryFamily } from './fabric-setup';
import { FontPicker } from './FontPicker';
import { fontWeightsFor } from './fonts';
import { groupColor } from './objects';
import type { BrandInfo, MaskKind } from './types';
import type { DesignEditorApi } from './useDesignEditor';
import { BarButton, BarDivider, ColorField, NumberField } from './ui';

/**
 * The contextual property bar above the canvas: the handful of controls you
 * reach for most for what is selected. Everything else lives in the inspector.
 * Fixed height so the canvas never jumps as the selection changes.
 */
export const ContextBar: React.FC<{
    ed: DesignEditorApi;
    brand: BrandInfo;
    swatches: string[];
    onOpenImages: () => void;
}> = ({ ed, brand, swatches, onOpenImages }) => {
    const { t } = useTranslation();
    const shell = 'flex h-12 shrink-0 items-center gap-1 overflow-x-auto overflow-y-hidden border-b bg-background px-3 [&::-webkit-scrollbar]:h-0';
    const o = ed.active;

    if (ed.cropping) {
        return (
            <div className={shell}>
                <Crop className="ms-1 size-4 text-foreground" />
                <span className="ms-1.5 text-xs text-foreground">{t('DESIGN_EDITOR.CROP_HINT')}</span>
                <div className="flex-1" />
                <button type="button" onClick={ed.cancelCrop} className="h-8 cursor-pointer rounded-lg border bg-background px-3 text-xs text-foreground hover:bg-accent">{t('DESIGN_EDITOR.CANCEL')}</button>
                <button type="button" onClick={ed.applyCrop} className="ms-1.5 h-8 cursor-pointer rounded-lg bg-foreground px-3 text-xs font-medium text-background hover:opacity-90">{t('DESIGN_EDITOR.CROP_APPLY')}</button>
            </div>
        );
    }

    if (!o || !ed.selection.length) {
        return (
            <div className={shell}>
                <ColorField
                    title={t('DESIGN_EDITOR.BACKGROUND_COLOR')}
                    value={(ed.canvas?.backgroundColor as any) || '#FFFFFF'}
                    onChange={(p) => ed.setArtboardColor(p as any)}
                    swatches={swatches}
                />
                <span className="ms-1 text-xs text-muted-foreground">{t('DESIGN_EDITOR.SELECT_HINT')}</span>
            </div>
        );
    }

    const multi = ed.selection.length > 1;
    const any = o as any;
    const text = !multi && isTextObject(o) ? (o as fabric.Textbox) : null;
    const image = !multi && isImageObject(o) ? (o as fabric.Image) : null;
    const iconGroup = !multi && o.type === 'group' && (any.csRole === 'icon' || any.csRole === 'divider');
    const shape = !multi && !text && !image && o.type !== 'group' && o.type !== 'activeSelection';
    const lineLike = o.type === 'line' || any.csShape === 'arrow' || any.csShape === 'frame';
    const textOnly = (x: fabric.Object) => isTextObject(x);

    return (
        <div className={shell}>
            {text && (() => {
                const family = primaryFamily(text.fontFamily);
                const weights = fontWeightsFor(family);
                const w = Number(text.fontWeight) || 400;
                const bold = w >= 600;
                const boldTarget = bold ? (weights.includes(400) ? 400 : weights[0]) : (weights.includes(700) ? 700 : weights[weights.length - 1]);
                const align = (text.textAlign || 'left') as string;
                const nextAlign = align === 'left' ? 'center' : align === 'center' ? 'right' : 'left';
                const AlignIcon = align === 'center' ? AlignCenter : align === 'right' ? AlignRight : AlignLeft;
                const s = text.text || '';
                const upper = s.length > 0 && s === s.toUpperCase() && s !== s.toLowerCase();
                return (
                    <>
                        <FontPicker value={family} onChange={(f) => { void ed.setFont(f); }} brandFont={brand.font} className="w-44" />
                        <NumberField label={t('DESIGN_EDITOR.SIZE_SHORT')} min={4} max={1000} value={text.fontSize || 24} onCommit={(v) => ed.update({ fontSize: v }, { filter: textOnly, history: 'now' })} className="w-[84px] shrink-0" />
                        <BarDivider />
                        <ColorField glyph="A" title={t('DESIGN_EDITOR.TEXT_COLOR')} value={text.fill as any} onChange={(p) => ed.update({ fill: p, csColorRole: undefined, csColorSet: true }, { filter: textOnly })} swatches={swatches} allowGradient />
                        <BarButton title={t('DESIGN_EDITOR.BOLD')} active={bold} disabled={weights.length < 2} onClick={() => { void ed.setTextStyle({ fontWeight: boldTarget }); }}><Bold /></BarButton>
                        <BarButton title={t('DESIGN_EDITOR.ITALIC')} active={text.fontStyle === 'italic'} onClick={() => { void ed.setTextStyle({ fontStyle: text.fontStyle === 'italic' ? 'normal' : 'italic' }); }}><Italic /></BarButton>
                        <BarButton title={t('DESIGN_EDITOR.UPPERCASE')} active={upper} onClick={() => ed.forEachSelected((x) => { if (isTextObject(x)) { const v = x.text || ''; x.set({ text: upper ? v.charAt(0) + v.slice(1).toLowerCase() : v.toUpperCase() }); } })}><CaseSensitive /></BarButton>
                        <BarButton title={t('DESIGN_EDITOR.TEXT_ALIGN')} onClick={() => ed.update({ textAlign: nextAlign }, { filter: textOnly, history: 'now' })}><AlignIcon /></BarButton>
                    </>
                );
            })()}

            {shape && (
                <>
                    {!lineLike && (
                        <ColorField title={t('DESIGN_EDITOR.FILL')} value={o.fill as any} onChange={(p) => ed.update({ fill: p, csColorRole: undefined })} swatches={swatches} allowGradient />
                    )}
                    <ColorField title={t('DESIGN_EDITOR.STROKE')} value={(o.stroke as any) || '#1A1917'} onChange={(p) => ed.update({ stroke: p, strokeWidth: o.strokeWidth || 2 })} swatches={swatches} />
                    <span className="ms-1 text-xs text-muted-foreground">{t('DESIGN_EDITOR.MORE_IN_INSPECTOR')}</span>
                </>
            )}

            {iconGroup && (
                <ColorField title={t('DESIGN_EDITOR.COLOR')} value={groupColor(o)} onChange={(p) => typeof p === 'string' && ed.setColor(p)} swatches={swatches} />
            )}

            {image && (
                <>
                    <BarButton title={t('DESIGN_EDITOR.CROP')} onClick={ed.startCrop} disabled={Math.abs(image.angle || 0) > 0.01 || !!any.csLocked}><Crop />{t('DESIGN_EDITOR.CROP')}</BarButton>
                    <BarButton title={t('DESIGN_EDITOR.REPLACE')} onClick={onOpenImages}><Wallpaper />{t('DESIGN_EDITOR.REPLACE')}</BarButton>
                    <BarButton title={t('DESIGN_EDITOR.FLIP_H')} onClick={() => ed.flip('h')} active={!!image.flipX}><FlipHorizontal2 /></BarButton>
                    <BarDivider />
                    {(['none', 'rounded', 'circle', 'arch'] as MaskKind[]).map((k) => (
                        <BarButton key={k} title={t(`DESIGN_EDITOR.MASK_${k.toUpperCase()}`)} active={(any.csMask || 'none') === k} onClick={() => ed.setMask(k)}>
                            <svg viewBox="0 0 20 20" className="size-4" fill="currentColor" opacity={0.75}>
                                {k === 'none' && <rect x="3" y="3" width="14" height="14" />}
                                {k === 'rounded' && <rect x="3" y="3" width="14" height="14" rx="4" />}
                                {k === 'circle' && <circle cx="10" cy="10" r="7" />}
                                {k === 'arch' && <path d="M4 18 V9 A6 6 0 0 1 16 9 V18 Z" />}
                            </svg>
                        </BarButton>
                    ))}
                </>
            )}

            {multi && (
                <>
                    <BarButton title={t('DESIGN_EDITOR.ALIGN_HCENTER')} onClick={() => ed.align('hcenter')}><AlignHorizontalJustifyCenter /></BarButton>
                    <BarButton title={t('DESIGN_EDITOR.ALIGN_VCENTER')} onClick={() => ed.align('vcenter')}><AlignVerticalJustifyCenter /></BarButton>
                    <BarDivider />
                    <BarButton title={t('DESIGN_EDITOR.GROUP')} onClick={ed.group}><Group />{t('DESIGN_EDITOR.GROUP')}</BarButton>
                </>
            )}
            {!multi && o.type === 'group' && !iconGroup && (
                <BarButton title={t('DESIGN_EDITOR.UNGROUP')} onClick={ed.ungroup}><Ungroup />{t('DESIGN_EDITOR.UNGROUP')}</BarButton>
            )}

            <div className="min-w-2 flex-1" />
            <BarButton title={t('DESIGN_EDITOR.DUPLICATE')} onClick={ed.duplicate}><Copy /></BarButton>
            <BarButton title={any.csLocked ? t('DESIGN_EDITOR.UNLOCK') : t('DESIGN_EDITOR.LOCK')} onClick={() => ed.toggleLock()}>{any.csLocked ? <Lock /> : <LockOpen />}</BarButton>
            <BarButton title={t('DESIGN_EDITOR.DELETE')} onClick={ed.deleteSelection} destructive><Trash2 /></BarButton>
        </div>
    );
};
